import React, { useEffect } from 'react';
import { Card, Typography, Divider, Space, Button, Input } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { webAppENV } from "../../../config/";

const { Title, Paragraph, Text } = Typography;

const RestaurantBookingEmbed = ({ restaurantId }) => {
    // Add the Poftigo script to the page for the live example
    useEffect(() => {
        // Clean up any existing Poftigo elements first
        const cleanup = () => {
            const existingElements = document.querySelectorAll('[data-poftigo-modal], [data-poftigo-backdrop]');
            existingElements.forEach(el => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });

            const existingScripts = document.querySelectorAll('script[src*="booking-embed"]');
            existingScripts.forEach(script => {
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            });
        };

        cleanup();

        // Create script element for Poftigo config
        const configScript = document.createElement('script');
        configScript.innerHTML = `
            window.PoftigoConfig = {
                restaurantToken: "${restaurantId}", 
                buttonText: "Rezervă o masă",
                buttonColor: "#2563eb",
                buttonTextColor: "#FFFFFF",
                embedUrl:"${webAppENV}booking/embed"
            };
        `;
        document.head.appendChild(configScript);

        // Create script element for Poftigo embed.js - CONSISTENT URL
        const embedScript = document.createElement('script');
        embedScript.src = webAppENV + "booking-embed.js"; // This should match the installation code
        embedScript.async = true;

        // Add error handling
        embedScript.onerror = () => {
            console.error('Failed to load Poftigo booking script');
        };

        embedScript.onload = () => {
            console.log('Poftigo booking script loaded successfully');
            // Re-trigger initialization if script loaded after DOM elements
            if (window.PoftigoInit && typeof window.PoftigoInit === 'function') {
                window.PoftigoInit();
            }
        };

        document.head.appendChild(embedScript);

        // Cleanup when component unmounts
        return () => {
            if (configScript.parentNode) {
                configScript.parentNode.removeChild(configScript);
            }
            if (embedScript.parentNode) {
                embedScript.parentNode.removeChild(embedScript);
            }
            cleanup();
            // Reset the global PoftigoConfig
            window.PoftigoConfig = undefined;
        };
    }, [restaurantId]);

    const [copied, setCopied] = React.useState({
        script: false,
        button: false,
        link: false,
        directLink: false
    });

    // FIXED: Use consistent URL in installation code
    const scriptCode = `<script>
// Configurare Poftigo
window.PoftigoConfig = {
  restaurantToken: "${restaurantId}", // Tokenul restaurantului dvs.
  buttonText: "Rezervă o masă",
  buttonColor: "#2563eb", // Schimbați pentru a se potrivi cu culoarea brandului dvs.
  buttonTextColor: "#FFFFFF",
  embedUrl:"${webAppENV}booking/embed"
};
</script>
<script async src="${webAppENV}booking-embed.js"></script>`;

    const buttonCode = `<div data-poftigo-button></div>`;
    const linkCode = `<a href="#" data-poftigo-link>Fă o rezervare</a>`;
    const customizationCode = `window.PoftigoConfig = {
  restaurantToken: "${restaurantId}",
  buttonText: "Rezervă acum", // Text personalizat pentru buton
  buttonColor: "#4F46E5", // Culoare indigo
  buttonTextColor: "#FFFFFF",
  modalZIndex: 9999, // Ajustați dacă există conflicte de z-index
  preloadModal: true, // Pre-încărcă modalul pentru o deschidere mai rapidă
}`;

    const copyToClipboard = (text, type) => {
        navigator.clipboard.writeText(text);
        setCopied({ ...copied, [type]: true });
        setTimeout(() => {
            setCopied({ ...copied, [type]: false });
        }, 2000);
    };

    return (
        <div className="booking-embed-container">
            <Paragraph>
                Urmați acești pași simpli pentru a adăuga formularul de rezervare pe site-ul dvs. web.
            </Paragraph>

            <Divider orientation="left">Moduri de implementare</Divider>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Direct Link Option */}
                <Card
                    title="Opțiunea 1: Link direct către pagina de rezervare"
                    style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                >
                    <Paragraph>
                        Cea mai simplă modalitate de a oferi rezervări online este prin folosirea unui link direct.
                        Această opțiune nu necesită instalarea de scripturi și funcționează pe orice platformă.
                    </Paragraph>

                    <div style={{ marginBottom: '1rem' }}>
                        <Text strong>Link-ul dvs. de rezervare:</Text>
                        <div className="code-block-container" style={{ marginTop: '0.5rem' }}>
                            <Input
                                value={`${webAppENV}booking/embed/${restaurantId}`}
                                readOnly
                                className="code-block"
                            />
                            <Button
                                type="primary"
                                icon={copied.directLink ? <CheckOutlined /> : <CopyOutlined />}
                                 onClick={() => copyToClipboard(`${webAppENV}booking/embed/${restaurantId}`, 'directLink')}
                                className="copy-button"
                            >
                                {copied.script ? "Copiat!" : "Copiază"}
                            </Button>
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <Text strong>Exemple de utilizare:</Text>
                        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                            <li>Adăugați link-ul în meniul site-ului dvs.</li>
                            <li>Includeți-l în postările de pe rețelele sociale</li>
                            <li>Folosiți-l în campanii de email marketing</li>
                        </ul>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <Button
                            type="primary"
                            href={`${webAppENV}booking/embed/${restaurantId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Testează link-ul direct
                        </Button>
                    </div>
                </Card>

                <Card title="Opțiunea 2: Widget integrat (pentru dezvoltatori)">
                    <Paragraph>
                        Pentru o experiență mai bună, puteți integra formularul de rezervare direct pe site-ul dvs.
                        folosind widget-ul nostru JavaScript. Aceasta necesită cunoștințe tehnice de bază.
                    </Paragraph>
                </Card>

                {/* Step 1 */}
                <div>
                    <Title level={4}>Pasul 1: Adăugați scriptul pe site-ul dvs.</Title>
                    <Paragraph>
                        Adăugați următorul cod chiar înainte de tag-ul <Text code>{'</body>'}</Text> pe site-ul dvs. web:
                    </Paragraph>
                    <div className="code-block-container">
                        <Input.TextArea
                            value={scriptCode}
                            autoSize={{ minRows: 8, maxRows: 12 }}
                            readOnly
                            className="code-block"
                        />
                        <Button
                            type="primary"
                            icon={copied.script ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => copyToClipboard(scriptCode, 'script')}
                            className="copy-button"
                        >
                            {copied.script ? "Copiat!" : "Copiază"}
                        </Button>
                    </div>
                    <Paragraph type="secondary">
                        Tokenul dvs. de restaurant este: <Text code>{restaurantId}</Text>
                    </Paragraph>
                </div>

                {/* Step 2 */}
                <div>
                    <Title level={4}>Pasul 2: Adăugați butoane pe pagina dvs.</Title>
                    <Paragraph>
                        Adăugați acest HTML acolo unde doriți să apară butoanele "Rezervă o masă":
                    </Paragraph>
                    <div className="code-block-container">
                        <Input.TextArea
                            value={buttonCode}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            readOnly
                            className="code-block"
                        />
                        <Button
                            type="primary"
                            icon={copied.button ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => copyToClipboard(buttonCode, 'button')}
                            className="copy-button"
                        >
                            {copied.button ? "Copiat!" : "Copiază"}
                        </Button>
                    </div>
                </div>

                {/* Step 3 */}
                <div>
                    <Title level={4}>Pasul 3: Adăugați link-uri text (Opțional)</Title>
                    <Paragraph>
                        Puteți transforma orice link pentru a deschide formularul de rezervare:
                    </Paragraph>
                    <div className="code-block-container">
                        <Input.TextArea
                            value={linkCode}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            readOnly
                            className="code-block"
                        />
                        <Button
                            type="primary"
                            icon={copied.link ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => copyToClipboard(linkCode, 'link')}
                            className="copy-button"
                        >
                            {copied.link ? "Copiat!" : "Copiază"}
                        </Button>
                    </div>
                </div>

                {/* Customization */}
                <div>
                    <Title level={4}>Opțiuni de personalizare</Title>
                    <Paragraph>
                        Puteți personaliza aspectul modificând obiectul PoftigoConfig:
                    </Paragraph>
                    <div className="code-block-container">
                        <Input.TextArea
                            value={customizationCode}
                            autoSize={{ minRows: 10, maxRows: 15 }}
                            readOnly
                            className="code-block"
                        />
                    </div>
                </div>
            </Space>

            <Card title="Exemplu live - Widget integrat" style={{ marginTop: '2rem' }}>
                <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <Paragraph>
                        Iată cum arată butonul de rezervare integrat. Apăsați butonul pentru a testa widget-ul:
                    </Paragraph>
                    <div className="demo-button" style={{ margin: '1rem 0' }}>
                        <div data-poftigo-button></div>
                    </div>
                    <Paragraph type="secondary" style={{ marginTop: '1rem' }}>
                        Acest buton deschide formularul într-un modal overlay, păstrând utilizatorii pe site-ul dvs.
                        Pentru comparație, <a href={`${webAppENV}booking/embed/${restaurantId}`} target="_blank" rel="noopener noreferrer">
                            accesați link-ul direct
                        </a> pentru a vedea diferența.
                    </Paragraph>

                </div>
            </Card>

            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <Paragraph type="secondary">
                    Aveți nevoie de ajutor? Contactați echipa de suport la <a href="mailto:contact@poftigo.ro">contact@poftigo.ro</a>
                </Paragraph>
            </div>
        </div>
    );
};

export default RestaurantBookingEmbed;